import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Snackbar,
  Alert,
  useTheme,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Footer from '../components/Footer';

const ContactPage = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    restaurant: '',
    plan: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!formData.restaurant) newErrors.restaurant = 'Restaurant name is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would typically send the form data to your backend
      console.log('Form submitted:', formData);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Thank you for contacting us! We will get back to you shortly.',
        severity: 'success',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        restaurant: '',
        plan: '',
        message: '',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: 'background.default',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box sx={{ pr: { md: 5 }, mb: { xs: 5, md: 0 } }}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                  }}
                >
                  Get in Touch
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph>
                  Have questions about our AR menu solution? Want to get started with your restaurant? We'd love to hear from you.
                </Typography>
                
                <Card sx={{ mb: 4, mt: 5, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white'
                        }}
                      >
                        <EmailIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email Us
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          <a 
                            href="mailto:info@qresto.com" 
                            style={{ 
                              textDecoration: 'none',
                              color: theme.palette.text.primary
                            }}
                          >
                            info@qresto.com
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white'
                        }}
                      >
                        <LocalPhoneIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Call Us
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          <a 
                            href="tel:+15551234567" 
                            style={{ 
                              textDecoration: 'none',
                              color: theme.palette.text.primary
                            }}
                          >
                            +1 (555) 123-4567
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white'
                        }}
                      >
                        <LocationOnIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Our Location
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          123 Restaurant Ave, Food City, FC 12345
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Restaurant QR code menu"
                  sx={{
                    width: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    display: { xs: 'none', lg: 'block' },
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                }}
              >
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  Contact Us
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Fill out this form and we'll get back to you as soon as possible.
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Optional"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Restaurant Name"
                        name="restaurant"
                        value={formData.restaurant}
                        onChange={handleChange}
                        error={Boolean(errors.restaurant)}
                        helperText={errors.restaurant}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <RestaurantIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="plan-select-label">Interested Plan</InputLabel>
                        <Select
                          labelId="plan-select-label"
                          id="plan-select"
                          name="plan"
                          value={formData.plan}
                          label="Interested Plan"
                          onChange={handleChange}
                        >
                          <MenuItem value="">
                            <em>Not sure yet</em>
                          </MenuItem>
                          <MenuItem value="basic">Basic (₹12,499)</MenuItem>
                          <MenuItem value="professional">Professional (₹24,999)</MenuItem>
                          <MenuItem value="premium">Premium (₹39,999)</MenuItem>
                          <MenuItem value="custom">I need a custom solution</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your restaurant and what you're looking for..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ py: 1.5, mt: 2 }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Footer />
    </>
  );
};

export default ContactPage;